// ABOUTME: Bulk change dialog with three steps: Scope, Actions, Preview & apply (the result shows in step 3).
// ABOUTME: The scan, apply and cancel jobs are started through callbacks of the host; the run is polled while active.

import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    Divider,
    Stack,
    Step,
    Stepper,
} from '@ds/coolshop';
import { JobDetailDialog } from '@modules/job-list';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { deleteFindReplaceRun, FindReplaceApiError } from '../api/findReplaceApi';
import { FindReplaceDialogRequest } from '../hooks/useFindReplaceDialog';
import { useFindReplaceOptions } from '../hooks/useFindReplaceOptions';
import { MAX_ROWS_PAGE_SIZE } from '../hooks/useFindReplaceRows';
import { useFindReplaceRun } from '../hooks/useFindReplaceRun';
import { useScopePreview } from '../hooks/useScopePreview';
import { BulkChangeAction, FindReplaceJobCallbacks, FindReplaceRunDto, RunInput } from '../types';
import { defaultAction, expandActions } from '../utils/actionDrafts';
import { buildContentTypeLabeler } from '../utils/contentTypeLabels';
import { buildScope, ScopeDraft, scopeDraftFrom } from '../utils/scopeDraft';
import { ActionsStep } from './ActionsStep';
import { BulkChangeRecap } from './BulkChangeRecap';
import { PreviewStep } from './PreviewStep';
import { ResultStep } from './ResultStep';
import { ScopeStep } from './ScopeStep';
import { StepNumberIcon } from './StepNumberIcon';

const DEFAULT_PAGE_SIZE = 50;

export type FindReplaceDialogProps = FindReplaceJobCallbacks & {
    open: boolean;
    onClose: () => void;
    /** The initial scope; its hierarchy, when given, is locked. */
    request: FindReplaceDialogRequest;
};

const isResultRun = (run: FindReplaceRunDto, applyRequested: boolean) =>
    applyRequested ||
    run.status === 'applying' ||
    run.status === 'applied' ||
    (run.status === 'cancelled' && !!run.result);

const draftOfRequest = (request: FindReplaceDialogRequest): ScopeDraft =>
    scopeDraftFrom({
        hierarchyId: request.hierarchyId,
        rootNodeId: request.rootNodeId,
        filters: request.filters,
        branchLabel: request.breadcrumb?.join(' › '),
    });

export function FindReplaceDialog({
    open,
    onClose,
    request,
    onScan,
    onApply,
    onCancel,
}: FindReplaceDialogProps) {
    const intl = useIntl();
    const { formatMessage } = intl;
    const { searchDataPagination } = useContext(SettingsContext);

    const [runId, setRunId] = useState<string | null>(request.runId ?? null);
    const [step, setStep] = useState<0 | 1>(0);
    const [draft, setDraft] = useState<ScopeDraft>(() => draftOfRequest(request));
    const [actions, setActions] = useState<BulkChangeAction[]>(() => [defaultAction('setValue')]);
    const [scanning, setScanning] = useState(false);
    const [scanError, setScanError] = useState<string | null>(null);
    const [applyRequested, setApplyRequested] = useState(false);
    const [applyJobId, setApplyJobId] = useState<string | null>(null);
    const [jobDialogId, setJobDialogId] = useState<string | null>(null);

    const {
        run,
        error: runError,
        refetch: refetchRun,
    } = useFindReplaceRun(open ? runId : null, applyRequested);

    // Opened on an existing run: its scope and actions are what "Edit actions" goes back to.
    useEffect(() => {
        if (!run || draft.hierarchyId) return;
        setDraft(scopeDraftFrom(run.input.scope));
        setActions(run.input.actions);
    }, [run, draft.hierarchyId]);

    useEffect(() => {
        if (run?.status === 'applying' && run.job) setApplyJobId(run.job.id);
    }, [run]);

    // Counted here, not in the Scope step: the recap needs the count on every step, also when the
    // dialog opens straight on a run.
    const scopePreview = useScopePreview(open ? buildScope(draft) : null);

    const hierarchyId = draft.hierarchyId || run?.hierarchyId || '';
    const { options, error: optionsError } = useFindReplaceOptions(
        open && hierarchyId ? hierarchyId : null,
    );

    const labeler = useMemo(
        () => buildContentTypeLabeler(options, intl.locale),
        [options, intl.locale],
    );
    const defaultPageSize = Math.min(
        searchDataPagination?.defaultPageSize ?? DEFAULT_PAGE_SIZE,
        MAX_ROWS_PAGE_SIZE,
    );

    const activeStep = runId ? 2 : step;

    const steps = [
        formatMessage({ id: 'bulkChange.step.scope', defaultMessage: 'Scope' }),
        formatMessage({ id: 'bulkChange.step.actions', defaultMessage: 'Actions' }),
        formatMessage({ id: 'bulkChange.step.preview', defaultMessage: 'Preview & apply' }),
    ];

    const handleScan = async () => {
        const scope = buildScope(draft);
        if (!scope) return;
        const input: RunInput = {
            scope,
            actions: expandActions(actions, options?.contentTypes ?? []),
        };
        setScanning(true);
        setScanError(null);
        try {
            const newRunId = await onScan(input);
            setApplyRequested(false);
            setApplyJobId(null);
            setRunId(newRunId);
        } catch (e) {
            setScanError(e instanceof Error ? e.message : String(e));
        } finally {
            setScanning(false);
        }
    };

    const handleApply = useCallback(
        async (id: string, reapprovePublish: boolean) => {
            const jobId = await onApply(id, reapprovePublish);
            if (jobId) setApplyJobId(jobId);
            setApplyRequested(true);
            refetchRun();
        },
        [onApply, refetchRun],
    );

    /** Back to the Actions step: the preview is discarded, computing it again creates a new run. */
    const handleEditActions = () => {
        if (run && run.status !== 'scanning' && run.status !== 'applying' && !run.result) {
            deleteFindReplaceRun(run.id).catch(() => undefined);
        }
        setApplyRequested(false);
        setRunId(null);
        setStep(draft.hierarchyId ? 1 : 0);
    };

    const runGone = runError instanceof FindReplaceApiError && runError.status === 404;

    const recap = (editing: 'scope' | 'actions') => (
        <BulkChangeRecap
            editing={editing}
            draft={draft}
            actions={actions}
            options={options}
            labeler={labeler}
            preview={scopePreview.preview}
            previewLoading={scopePreview.loading}
            onEditScope={editing === 'actions' ? () => setStep(0) : undefined}
        />
    );

    const renderRunStep = () => {
        if (!run) {
            if (runError) {
                return (
                    <Stack gap={2} p={3}>
                        <Alert severity="error">
                            {runGone
                                ? formatMessage({
                                      id: 'findReplace.run.notFound',
                                      defaultMessage:
                                          'This preview no longer exists: it expired or was discarded.',
                                  })
                                : runError.message}
                        </Alert>
                        <Box>
                            <Button variant="contained" onClick={handleEditActions}>
                                {formatMessage({
                                    id: 'bulkChange.run.newBulkChange',
                                    defaultMessage: 'New bulk change',
                                })}
                            </Button>
                        </Box>
                    </Stack>
                );
            }
            return (
                <Box p={3}>
                    <CircularProgress size="medium" />
                </Box>
            );
        }
        if (isResultRun(run, applyRequested)) {
            return (
                <ResultStep
                    run={run}
                    hierarchyId={hierarchyId}
                    labeler={labeler}
                    defaultPageSize={defaultPageSize}
                    onRunChanged={refetchRun}
                    onCancel={onCancel}
                    onClose={onClose}
                    onOpenJob={applyJobId ? () => setJobDialogId(applyJobId) : undefined}
                />
            );
        }
        return (
            <PreviewStep
                run={run}
                hierarchyId={hierarchyId}
                labeler={labeler}
                defaultPageSize={defaultPageSize}
                onRunChanged={refetchRun}
                onCancel={onCancel}
                onApply={handleApply}
                onEditActions={handleEditActions}
            />
        );
    };

    const renderStep = () => {
        if (runId) return renderRunStep();
        if (step === 0) {
            return (
                <ScopeStep
                    draft={draft}
                    onChange={setDraft}
                    hierarchyLocked={!!request.hierarchyId}
                    options={options}
                    optionsError={optionsError}
                    labeler={labeler}
                    scopePreview={scopePreview}
                    sidebar={recap('scope')}
                    onCancel={onClose}
                    onNext={() => setStep(1)}
                />
            );
        }
        return (
            <ActionsStep
                actions={actions}
                onChange={setActions}
                options={options}
                labeler={labeler}
                sidebar={recap('actions')}
                onBack={() => setStep(0)}
                onNext={handleScan}
                computing={scanning}
                computeError={scanError}
            />
        );
    };

    return (
        <Dialog
            open={open}
            // A click outside must not throw away the bulk change being defined.
            onClose={(_event, reason) => {
                if (reason !== 'backdropClick') onClose();
            }}
            maxWidth="lg"
            fullWidth
            disablePaddings
            header={{
                title: formatMessage({ id: 'findReplace.title', defaultMessage: 'Bulk change' }),
                onClose,
            }}
        >
            {/* Taller than any screen, it shrinks to the dialog's maximum height: the height stays
                the same whatever the step shows, so editing never resizes the dialog. */}
            <Stack flex="1 1 auto" minHeight={0} height="100vh">
                <Box px={3} pt={1} pb={2}>
                    <Stepper activeStep={activeStep}>
                        {steps.map((title, index) => (
                            <Step
                                key={title}
                                title={title}
                                icon={
                                    index === activeStep ? (
                                        <StepNumberIcon number={index + 1} />
                                    ) : undefined
                                }
                            />
                        ))}
                    </Stepper>
                </Box>
                <Divider />
                <Box display="flex" flexDirection="column" flex={1} minHeight={0} overflow="auto">
                    {renderStep()}
                </Box>
            </Stack>
            {jobDialogId && (
                <JobDetailDialog jobId={jobDialogId} onClose={() => setJobDialogId(null)} />
            )}
        </Dialog>
    );
}
